import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  getDoctorByUserId,
  getDoctorDocuments,
  updateDoctor,
  type DoctorDTO,
} from '@/services/doctors';
import { MedicalCredentialsUpload } from '@/components/dashboard/MedicalCredentialsUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCpf, formatPhone, stripMask } from '@/utils/masks';
import {
  BRAZIL_UF_OPTIONS,
  DOCTOR_ONBOARDING_CACHE_KEY,
  DOCTOR_SPECIALTIES,
  doctorOnboardingSteps,
  hasAllDoctorOnboardingDocuments,
} from '@/lib/doctorOnboarding';

function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

export default function DoctorChecklistPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [doctor, setDoctor] = useState<DoctorDTO | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [allDocsUploaded, setAllDocsUploaded] = useState(false);

  const [cpf, setCpf] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [sex, setSex] = useState<'M' | 'F' | ''>('');
  const [phone, setPhone] = useState('');
  const [officeStreet, setOfficeStreet] = useState('');
  const [officeCity, setOfficeCity] = useState('');
  const [officeState, setOfficeState] = useState('');
  const [specialty, setSpecialty] = useState('');

  const currentStep = doctorOnboardingSteps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / doctorOnboardingSteps.length) * 100);
  const isLastStep = stepIndex === doctorOnboardingSteps.length - 1;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.id) {
        setIsLoadingProfile(false);
        return;
      }
      setIsLoadingProfile(true);
      try {
        const response = await getDoctorByUserId(user.id);
        if (cancelled) return;
        if (!response.success || !response.data) {
          toast.error(response.error || 'Não foi possível carregar o perfil médico.');
          return;
        }
        const profile = response.data;
        setDoctor(profile);
        setCpf(formatCpf(profile.cpf || '') || '');
        setDateOfBirth(toDateInputValue(profile.dateOfBirth));
        setSex((profile.sex as 'M' | 'F' | undefined) || '');
        setPhone(formatPhone(profile.phone || '') || '');
        setOfficeStreet(profile.officeStreet || profile.clinicAddress || '');
        setOfficeCity(profile.officeCity || '');
        setOfficeState(profile.officeState || '');
        setSpecialty(profile.specialty || '');

        const docsRes = await getDoctorDocuments(profile.id);
        if (!cancelled && docsRes.success && Array.isArray(docsRes.data)) {
          setAllDocsUploaded(hasAllDoctorOnboardingDocuments(docsRes.data));
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) toast.error('Erro ao carregar onboarding do médico.');
      } finally {
        if (!cancelled) setIsLoadingProfile(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const identityValid = useMemo(() => {
    const cpfDigits = stripMask(cpf);
    const phoneDigits = stripMask(phone);
    return (
      cpfDigits.length === 11 &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth) &&
      (sex === 'M' || sex === 'F') &&
      phoneDigits.length >= 10 &&
      officeCity.trim().length > 0 &&
      officeState.trim().length === 2
    );
  }, [cpf, dateOfBirth, sex, phone, officeCity, officeState]);

  const saveIdentityAndSpecialty = async (): Promise<boolean> => {
    if (!doctor?.id) {
      toast.error('Perfil de médico não encontrado.');
      return false;
    }

    const cpfDigits = stripMask(cpf);
    const phoneDigits = stripMask(phone);
    if (cpfDigits.length !== 11) {
      toast.error('Informe um CPF válido com 11 dígitos.');
      return false;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      toast.error('Informe a data de nascimento.');
      return false;
    }
    if (sex !== 'M' && sex !== 'F') {
      toast.error('Selecione o sexo (M ou F).');
      return false;
    }
    if (phoneDigits.length < 10) {
      toast.error('Informe o telefone com DDD.');
      return false;
    }
    if (!officeCity.trim() || officeState.trim().length !== 2) {
      toast.error('Informe cidade e UF.');
      return false;
    }
    if (!specialty.trim()) {
      toast.error('Selecione a especialidade.');
      return false;
    }

    setIsSaving(true);
    try {
      const response = await updateDoctor(doctor.id, {
        cpf: cpfDigits,
        dateOfBirth,
        sex,
        phone: phoneDigits,
        officeStreet: officeStreet.trim() || undefined,
        officeCity: officeCity.trim(),
        officeState: officeState.trim().toUpperCase(),
        specialty: specialty.trim(),
      });
      if (!response.success || !response.data) {
        toast.error(
          typeof response.error === 'string'
            ? response.error
            : 'Não foi possível salvar os dados.',
        );
        return false;
      }
      setDoctor(response.data);
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Erro de rede ao salvar o perfil.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep.id === 'identity') {
      if (!identityValid) {
        toast.error('Preencha CPF, nascimento, sexo, telefone, cidade e UF.');
        return;
      }
      setStepIndex((i) => i + 1);
      return;
    }

    if (currentStep.id === 'specialty') {
      if (!specialty.trim()) {
        toast.error('Selecione a especialidade.');
        return;
      }
      const saved = await saveIdentityAndSpecialty();
      if (!saved) return;
      setStepIndex((i) => i + 1);
      return;
    }

    if (currentStep.id === 'credentials') {
      await finalizeOnboarding();
    }
  };

  const finalizeOnboarding = async () => {
    if (!doctor?.id || !user?.id) return;

    if (!allDocsUploaded) {
      const docsRes = await getDoctorDocuments(doctor.id);
      const ok =
        docsRes.success &&
        Array.isArray(docsRes.data) &&
        hasAllDoctorOnboardingDocuments(docsRes.data);
      if (!ok) {
        toast.error(
          'Envie todos os documentos: CRM, especialidade, inscrição e vídeo de 3s.',
        );
        return;
      }
      setAllDocsUploaded(true);
    }

    const saved = await saveIdentityAndSpecialty();
    if (!saved) return;

    localStorage.setItem(DOCTOR_ONBOARDING_CACHE_KEY(user.id), 'false');
    toast.success('Cadastro enviado para análise. Você já pode usar o painel.');
    navigate('/dashboard', { replace: true });
  };

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950 p-4">
        <div className="max-w-md text-center space-y-3">
          <p className="text-gray-700 dark:text-gray-200">
            Não encontramos o perfil médico vinculado a esta conta.
          </p>
          <Button onClick={() => navigate('/dashboard')}>Ir ao painel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white dark:from-neutral-950 dark:to-neutral-900">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-sm font-medium text-teal-700 dark:text-teal-400">
            Onboarding médico
          </p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            Complete seu cadastro
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Dados da Memed, especialidade e credenciais — uma única vez.
          </p>
        </div>

        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>
              Etapa {stepIndex + 1} de {doctorOnboardingSteps.length}: {currentStep.title}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 dark:bg-neutral-800 overflow-hidden">
            <div
              className="h-full bg-teal-600 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            {doctorOnboardingSteps.map((step, index) => (
              <span
                key={step.id}
                className={`text-xs px-2 py-1 rounded-md ${
                  index === stepIndex
                    ? 'bg-teal-600 text-white'
                    : index < stepIndex
                      ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                      : 'bg-gray-100 text-gray-500 dark:bg-neutral-800 dark:text-gray-400'
                }`}
              >
                {step.title}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-2xl p-6 shadow-sm space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {currentStep.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{currentStep.subtitle}</p>
          </div>

          {currentStep.id === 'identity' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>CPF *</Label>
                  <Input
                    value={cpf}
                    onChange={(e) => setCpf(formatCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data de nascimento *</Label>
                  <Input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sexo *</Label>
                  <select
                    value={sex}
                    onChange={(e) => setSex(e.target.value as 'M' | 'F' | '')}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
                  >
                    <option value="">Selecione</option>
                    <option value="M">Masculino (M)</option>
                    <option value="F">Feminino (F)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefone *</Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    inputMode="tel"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Endereço</Label>
                  <Input
                    value={officeStreet}
                    onChange={(e) => setOfficeStreet(e.target.value)}
                    placeholder="Rua / Avenida"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cidade *</Label>
                  <Input
                    value={officeCity}
                    onChange={(e) => setOfficeCity(e.target.value)}
                    placeholder="Cidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UF *</Label>
                  <select
                    value={officeState}
                    onChange={(e) => setOfficeState(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
                  >
                    <option value="">Selecione</option>
                    {BRAZIL_UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {currentStep.id === 'specialty' && (
            <div className="space-y-2">
              <Label>Especialidade *</Label>
              <select
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
              >
                <option value="">Selecione</option>
                {DOCTOR_SPECIALTIES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                CRM e UF já foram informados no cadastro. Aqui definimos a área de atuação.
              </p>
            </div>
          )}

          {currentStep.id === 'credentials' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Envie CRM, certificado de especialidade, comprovante de inscrição e vídeo de
                confirmação (3s). A assinatura de receita/atestado/exame é na Memed (Bird ID), não
                neste cadastro.
              </p>
              <MedicalCredentialsUpload
                doctorId={doctor.id}
                onAllDocumentsUploaded={() => setAllDocsUploaded(true)}
              />
              {allDocsUploaded && (
                <p className="text-sm text-teal-700 dark:text-teal-300 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Todos os documentos enviados.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={isSaving || stepIndex === 0}
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              type="button"
              disabled={isSaving || (isLastStep && !allDocsUploaded)}
              onClick={() => void handleNext()}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando…
                </>
              ) : isLastStep ? (
                <>
                  Concluir
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Continuar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
